/**
 * DE-171 — pure sync-status logic for the DeviceContentCard badge.
 *
 * Factored out of the component so it can be unit-tested without a React/render
 * harness (the repo's tests are pure-logic, no Testing Library). The card maps
 * the returned state through CONTENT_BADGE to a pill.
 *
 * Semantics: compare the assigned profile IDENTITY (name + version) against what
 * the device reports as applied (`content.appliedProfile`, with a per-collection
 * `profileVersion` fallback for partial applies):
 *   in-sync  — applied profile version matches the assignment.
 *   pending  — applied profile version is present but DIFFERS (capable device,
 *              content not yet applied / installs at next power-on).
 *   unknown  — device reports NO profile identity at all (telemetry/firmware too
 *              old to emit appliedProfile) — missing info, NOT a false pending.
 *   factory  — nothing is assigned (shipped baseline, not an error).
 *
 * Backward-compat: all field accesses are null-safe; an old-shape shadow (no
 * appliedProfile / profileVersion) resolves to `unknown` and never throws.
 */
export type ContentSyncState = 'factory' | 'in-sync' | 'pending' | 'unknown';

export interface AppliedContent {
  appliedProfile?: { name?: string | null; version?: string | null } | null;
  collections?: Record<
    string,
    { version?: string | null; profileVersion?: string | null; managed?: boolean } | undefined
  >;
}
export interface AssignedContent {
  profile_version: string;
  profile_name?: string | null;
}

export function contentSyncStatus(
  applied: AppliedContent | null | undefined,
  assigned: AssignedContent | null | undefined,
): ContentSyncState {
  // Nothing assigned → baseline shipped content (not a pending managed update).
  if (!assigned) return 'factory';
  const wantVer = assigned.profile_version;
  const wantName = assigned.profile_name;

  // The identity the device reports it is running, with a per-collection fallback
  // (a partial/mixed apply may carry profileVersion per collection before the
  // top-level appliedProfile settles).
  const appliedVer = applied?.appliedProfile?.version;
  const appliedName = applied?.appliedProfile?.name;
  const collVers = Object.values(applied?.collections ?? {})
    .map((c) => c?.profileVersion)
    .filter((v): v is string => Boolean(v));

  const reportedVers = [appliedVer, ...collVers].filter((v): v is string => Boolean(v));
  // Device reports no profile identity → too old to report (missing info).
  if (reportedVers.length === 0) return 'unknown';

  const versionMatch = reportedVers.includes(wantVer);
  // When the name is known and the device reports one, require it to match too.
  const nameOk = !wantName || !appliedName || appliedName === wantName;
  return versionMatch && nameOk ? 'in-sync' : 'pending';
}

export const CONTENT_BADGE: Record<ContentSyncState, { label: string; cls: string }> = {
  factory: { label: 'Factory content', cls: 'pill-muted' },
  'in-sync': { label: 'In sync', cls: 'pill-accent' },
  pending: { label: 'Pending — installs at next mains connection', cls: 'pill-warn' },
  unknown: { label: 'Version unknown — update firmware', cls: 'pill-muted' },
};

/**
 * Map an AWS IoT job-execution status to the shared pill grammar
 * (pill-accent / pill-warn / pill-muted). Used by the Releases page deployment
 * ledger and the per-device active-updates card (DeviceUpdateJobs); release,
 * content_sync, mender_install, and Greengrass jobs share this status set.
 *
 * `status_details` (the IoT execution detailsMap) becomes the hover title.
 * Callers can override the empty (`nullLabel`) and `queued` labels for context
 * (e.g. content's "installs next power-on").
 */
/**
 * DE-186 — outcome pill for a SUCCEEDED release deployment. The device reports
 * what actually happened in statusDetails: `activation: "applied"` (forced
 * apply ran), `applySkipped`/`applyError` (forced, degraded to staged), or
 * `reason: "staged"` (plain stage — or an OLD agent that silently ignored the
 * force flag: that's the "Staged only" tell). Returns null when the details
 * don't describe a non-OS staging outcome (OS releases, group jobs with no
 * per-thing details, non-SUCCEEDED states) — the caller falls back to jobPill.
 */
/** What the device reports about the FACTORY content it was manufactured with. */
export interface FactoryContent {
  version?: string | null;
  image?: string | null;
}

/**
 * The last factory content set that shipped without a `factory-content.json`.
 *
 * Devices are manufactured by flashing an SD-card image. Images from telemetry
 * 2.9.0 / the factory-content recipe onward write that file, so the device can
 * name its own content set. Anything flashed earlier cannot — and that ABSENCE
 * is itself the answer: 2.2.0 is the only factory content set ever manufactured,
 * so a device that reports nothing is a device holding 2.2.0.
 *
 * The inference lives HERE and not on the device on purpose: a device should
 * report what it can actually see and never claim a version it was never told.
 * The fleet-level fact that "absent means 2.2.0" is knowledge the Portal has and
 * the device does not.
 *
 * WHAT WOULD INVALIDATE THIS: a second factory set reaching manufacturing while
 * some units still ship without the file. That cannot happen through the normal
 * path, because writing the file and changing the content are the same recipe —
 * but if it ever does, this constant is the single place to revisit.
 */
export const PRE_VERSIONING_FACTORY_CONTENT = '2.2.0';

/**
 * Human label for a device's factory content set, resolving the absent case.
 *
 * Returns the reported version verbatim when the device names one, otherwise the
 * inferred pre-versioning set marked as inferred — the mark matters, because a
 * reported 2.2.0 and an assumed 2.2.0 are different degrees of certainty and an
 * operator chasing a content bug needs to know which they are looking at.
 */
export function factoryContentLabel(fc: FactoryContent | null | undefined): {
  version: string;
  inferred: boolean;
} {
  const v = typeof fc?.version === 'string' ? fc.version.trim() : '';
  return v
    ? { version: v, inferred: false }
    : { version: PRE_VERSIONING_FACTORY_CONTENT, inferred: true };
}

/**
 * A stable identity for each outcome, independent of the words on the pill.
 *
 * `memberOutcomeBucket` used to classify by comparing `label` against the
 * literal strings 'Applied' / 'Staged'. Rewording a label therefore silently
 * reclassified every staged member as `staged-later` and corrupted the group
 * rollout tally — a display change quietly changing reported numbers. Switch on
 * `kind`; leave `label` free to say whatever reads best.
 */
export type OutcomeKind =
  | 'applied'
  | 'staged'
  | 'staged-only'
  | 'apply-failed'
  | 'apply-skipped'
  | 'unsupported';

export interface OutcomePill {
  kind: OutcomeKind;
  label: string;
  cls: string;
  title?: string;
}

// Why "Staged" alone was not good enough (2026-08-31): content applies at the
// next power-on, by which time the job execution is already terminal — the
// agent's work ended at staging and a completed execution's statusDetails can
// never be revised. So a bare "Staged" is permanent, including long after the
// content is live, and it reads as "this did not happen". A three-day-old
// staged row on …032 sent an investigation down the wrong path for an hour.
// The label now says what it means; the tooltip says where the truth lives.
const STAGED_NOTE =
  'Staged on the device and applies at the next power-on. This job cannot report ' +
  'that later apply — it is already complete — so if the device has since ' +
  'rebooted the content is probably live. The device Content card shows what is ' +
  'actually on disk.';

export function releaseOutcomePill(
  status: string | null | undefined,
  statusDetails: Record<string, string> | null | undefined,
  force: boolean,
): OutcomePill | null {
  const detailText = statusDetails
    ? Object.entries(statusDetails)
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ')
    : undefined;
  const title = detailText;
  // ENTIRELY-IGNORED — firmware predates the `release` op, so the device
  // REJECTED it (statusDetails: reason="unsupported operation release").
  // Nothing was staged; the operator must OS-update the device first.
  if (status === 'REJECTED')
    return { kind: 'unsupported', label: 'Ignored (unsupported)', cls: 'pill-warn', title };
  if (status !== 'SUCCEEDED' || !statusDetails) return null;
  if (statusDetails.activation === 'applied')
    return { kind: 'applied', label: 'Applied', cls: 'pill-accent', title };
  if (statusDetails.applyError)
    return {
      kind: 'apply-failed',
      label: 'Staged — apply failed',
      cls: 'pill-warn',
      title,
    };
  if (statusDetails.applySkipped)
    return { kind: 'apply-skipped', label: 'Staged — apply skipped', cls: 'pill-warn', title };
  const staged =
    statusDetails.reason === 'staged' || statusDetails.activation === 'next_mains_connection';
  if (staged)
    return force
      ? // forced, but the device ignored it (pre-aws.80 firmware)
        { kind: 'staged-only', label: 'Staged only', cls: 'pill-warn', title }
      : {
          kind: 'staged',
          label: 'Staged for next power-on',
          cls: 'pill-accent',
          title: detailText ? `${STAGED_NOTE}\n\n${detailText}` : STAGED_NOTE,
        };
  return null;
}

/**
 * DE-171 — classify ONE group member's release outcome into the three buckets
 * the Releases ledger groups by:
 *   worked       — applied now, or properly staged (unforced) → the update took.
 *   staged-later — forced, but the device staged instead of applying now (older
 *                  firmware, or apply skipped/failed). NOT a failure: it applies
 *                  at next power-on / via on-device Apply Now. ("only force ignored")
 *   ignored      — REJECTED (firmware predates the release op → nothing happened),
 *                  or a genuine FAILED/TIMED_OUT/CANCELED/REMOVED.
 *   pending      — still QUEUED / IN_PROGRESS (offline or not yet reported).
 */
export type OutcomeBucket = 'worked' | 'staged-later' | 'ignored' | 'pending';

export function memberOutcomeBucket(
  status: string | null | undefined,
  statusDetails: Record<string, string> | null | undefined,
  force: boolean,
): OutcomeBucket {
  if (status === 'REJECTED') return 'ignored';
  if (status === 'QUEUED' || status === 'IN_PROGRESS') return 'pending';
  if (status === 'SUCCEEDED') {
    // Switch on `kind`, never on `label`. The previous version compared against
    // the literal strings 'Applied' / 'Staged', so rewording a pill silently
    // moved members between buckets and changed the reported rollout tally.
    const kind = releaseOutcomePill(status, statusDetails, force)?.kind;
    return kind === 'applied' || kind === 'staged' ? 'worked' : 'staged-later';
  }
  // FAILED / TIMED_OUT / CANCELED / REMOVED / UNKNOWN — treat as not-converged.
  return 'ignored';
}

export function jobPill(
  job: { status: string; status_details?: Record<string, string> | null } | null,
  opts?: { nullLabel?: string; queuedLabel?: string },
): { label: string; cls: string; title?: string } {
  if (!job) return { label: opts?.nullLabel ?? 'No job', cls: 'pill-muted' };
  const detail = job.status_details
    ? Object.entries(job.status_details)
        .map(([k, v]) => `${k}: ${v}`)
        .join(' · ')
    : undefined;
  switch (job.status) {
    case 'QUEUED':
      return { label: opts?.queuedLabel ?? 'Queued', cls: 'pill-accent', title: detail };
    case 'IN_PROGRESS':
      return { label: 'In progress', cls: 'pill-accent', title: detail };
    case 'SUCCEEDED':
      return { label: 'Succeeded', cls: 'pill-accent', title: detail };
    case 'FAILED':
    case 'TIMED_OUT':
    case 'REJECTED': {
      // A download the agent gave up on (agent ≥1.9.0, after ~19 h of
      // escalating retries) is worth naming: a bare "Failed" reads as a broken
      // artifact, when in fact the artifact is fine and the device's link is
      // not. This is the ONLY place that state is ever visible — a FAILED job
      // drops out of the device page's Active-updates card, which is typed
      // QUEUED | IN_PROGRESS.
      if (job.status === 'FAILED' && job.status_details?.reason === 'download_exhausted') {
        const cycles = job.status_details.cycles;
        return {
          label: 'Download gave up',
          cls: 'pill-warn',
          title: [
            cycles
              ? `The device could not download the artifact after ${cycles} attempts.`
              : 'The device could not download the artifact.',
            'Its connection, not the release, is the problem. Re-issue once the link is healthy — the partial download is kept, so it resumes rather than starting over.',
            detail,
          ]
            .filter(Boolean)
            .join('\n\n'),
        };
      }
      return {
        label: job.status === 'FAILED' ? 'Failed' : job.status === 'TIMED_OUT' ? 'Timed out' : 'Rejected',
        cls: 'pill-warn',
        title: detail,
      };
    }
    default:
      return { label: job.status.toLowerCase(), cls: 'pill-muted', title: detail };
  }
}
